import {view, tokens, Icon, Link} from '@sail/ui';
import {support} from '@sail/icons/react/Icon';
import {document, api, list} from '@sail/icons/react/Icon__xsmall';
import {StripeAssistantLiveAnswersSource} from '@stripe-internal/conversations-client';
import {IconAsset} from '@sail/icons/types';
import {useAnalytics} from '../../AnalyticsProvider';

type SourceLinkPrefix = {
  icon: IconAsset;
  iconColor: string;
  text?: string;
  textColor?: string;
};

const collectionToPrefix = new Map<string, SourceLinkPrefix>(
  Object.entries({
    support_docs: {
      icon: support,
      iconColor: tokens.color.brand[500].toString(),
    },
    express_support_docs: {
      icon: support,
      iconColor: tokens.color.brand[500].toString(),
    },
    support_topic_pages: {
      icon: list,
      iconColor: tokens.color.feedback.info[300].toString(),
      text: 'Topic: ',
      textColor: tokens.color.feedback.info[600].toString(),
    },
    link_support_docs: {
      icon: support,
      iconColor: '#00C767', // Link brand300
    },
    stripe_docs: {
      icon: document,
      iconColor: tokens.color.hue.blue[600].toString(),
      text: 'Docs: ',
      textColor: tokens.color.hue.blue[600].toString(),
    },
    stripe_api_docs: {
      icon: api,
      iconColor: 'action.primary',
      text: 'API: ',
      textColor: 'action.primary',
    },
  }),
);

// Used if we can't find a match for the collection or a collection isn't returned by the backend.
const defaultPrefix = {
  icon: support,
  iconColor: tokens.color.brand[600].toString(),
};

export const SourceLink = ({
  source,
}: {
  source: StripeAssistantLiveAnswersSource;
}) => {
  const analytics = useAnalytics();

  const prefix: SourceLinkPrefix = source.collection
    ? collectionToPrefix.get(source.collection) || defaultPrefix
    : defaultPrefix;

  return (
    <view.div
      css={{
        stack: 'x',
        alignY: 'center',
        alignX: 'start',
        gap: 'small',
      }}
    >
      <Icon
        icon={prefix.icon}
        size="xsmall"
        css={{
          color: prefix.iconColor,
          minWidth: 12,
        }}
      />
      <Link
        href={source.url}
        target={source.url}
        onPress={() => {
          analytics.action(
            'universal_help_live_answers_frontend_sources_link_clicked',
            {
              live_answers_source_url: source.url,
              live_answers_source_title: source.title,
              live_answers_source_collection: source.collection,
              live_answers_source_score: source.score,
            },
          );
        }}
        css={{
          fontFamily: 'label.medium',
          fontSize: 12,
          color: 'text',
          fontWeight: 600,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          ':hover': {
            color: 'text',
          },
        }}
      >
        <view.span css={{color: prefix.textColor || 'text'}}>
          {prefix.text}
        </view.span>
        {source.title}
      </Link>
    </view.div>
  );
};
